/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React, { useState, useEffect } from 'react';

import { Card, Image, Form, InputGroup } from 'react-bootstrap';
import { PaperClipIcon } from '@heroicons/react/solid';

import * as S from '../../styles';
import { FeedbackOtherLabel } from '../../styles';

export interface IChoosePhotoProps {
    id: string;
    fontScale?: number;

    label?: string;
    labelColor?: string;
    errorText?: string;
    title?: string;
    value?: string;
    required?: boolean;
    chooseImageLabel?: string;
    chooseImageTooltip?: string;
    multiple?: boolean;
    onCompChange?(
        id: string,
        file: {
            name: string;
            type: string;
            size: string;
            base64: string;
            file: string;
        }
    ): void;
    hasError?: boolean;
}

export const ChoosePhoto = ({
    id,
    fontScale,

    label,
    labelColor,
    errorText,
    required,
    title,
    value,
    chooseImageLabel,
    chooseImageTooltip,
    multiple,
    onCompChange,
    hasError,
}: IChoosePhotoProps) => {
    const [selectedPhoto, setSelectedPhoto] = useState(value);
    useEffect(() => {
        onCompChange && onCompChange(id, value);
    }, [value]);
    const onChange = (e: any) => {
        let files = e.target.files;
        const allFiles: any = [];
        for (let i = 0; i < files.length; i++) {
            let file = files[i];

            // Make new FileReader
            let reader = new FileReader();

            // Convert the file to base64 text
            reader.readAsDataURL(file);

            reader.onload = () => {
                // Make a fileInfo Object
                let fileInfo = {
                    name: file.name,
                    type: file.type,
                    size: Math.round(file.size / 1000) + ' kB',
                    base64: reader.result,
                    file: file,
                };

                // Push it to the state
                allFiles.push(fileInfo);

                // If all files have been proceed
                if (allFiles.length == files.length) {
                    // Apply Callback function
                    if (multiple) {
                        onCompChange && onCompChange(id, allFiles);
                    } else {
                        onCompChange && onCompChange(id, allFiles[0]);
                    }
                    setSelectedPhoto(allFiles[0]?.base64);
                }
            }; // reader.onload
        } // for
    };

    const _renderLabelIf = () => {
        if (label) {
            return (
                <S.FormLabel fontScale={fontScale} color={labelColor}>
                    {label}
                    {required && <sup style={{ color: 'red' }}>*</sup>}
                </S.FormLabel>
            );
        }
        return null;
    };

    const _renderErrorTextIf = () => {
        if (hasError && errorText) {
            return (
                <S.FeedbackOtherLabel type="tooltip" fontScale={fontScale}>
                    {errorText}
                </S.FeedbackOtherLabel>
            );
        }
        return null;
    };

    return (
        <>
            <Form.Group id={id} className="formgroup">
                {_renderLabelIf()}
                <InputGroup>
                    <Card border="0" className="mb-4">
                        <Card.Body>
                            {/*<h6 className="mb-4">{title}</h6>*/}
                            <div className="d-flex align-items-center">
                                <div className="me-3">
                                    <Image fluid rounded src={selectedPhoto} />
                                </div>
                                <div className="file-field">
                                    <div className="d-flex justify-content-xl-center ms-xl-3">
                                        <div className="d-flex">
                                            <PaperClipIcon className="icon text-gray-500 me-2" />
                                            <input
                                                type="file"
                                                accept={'image/*'}
                                                onChange={onChange}
                                            />
                                            <div className="d-md-block text-left">
                                                <div className="fw-normal text-dark mb-1">
                                                    {chooseImageLabel}
                                                </div>
                                                <div className="text-gray small">
                                                    {chooseImageTooltip}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                    <InputGroup>{_renderErrorTextIf()}</InputGroup>
                </InputGroup>
            </Form.Group>
        </>
    );
};

ChoosePhoto.defaultProps = {
    required: false,
    title: 'Select Photo',
    value: 'https://s3.ap-south-1.amazonaws.com/io.vlinder.images/placeholder.jpeg',
    chooseImageLabel: 'Choose Image',
    chooseImageTooltip: 'JPG, GIF or PNG. Max size of 800K',
    multiple: false,
};
